import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  alpha,
  useTheme,
  Chip,
  CircularProgress,
  Grid,
} from "@mui/material";
import { LocalOffer, CheckCircle } from "@mui/icons-material";
import DiscountService from "../../../../services/DiscountService";

/**
 * DiscountSelector Component
 * Allows selection of available discounts
 */
const DiscountSelector = ({
  discountType,
  setDiscountType,
  discountValue,
  setDiscountValue,
  discountCode,
  setDiscountCode,
  orderTotal,
  hasCustomerMobile = false,
}) => {
  const theme = useTheme();
  const [discounts, setDiscounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDiscount, setSelectedDiscount] = useState(null);

  useEffect(() => {
    fetchDiscounts();
  }, []);

  const fetchDiscounts = async () => {
    try {
      setLoading(true);
      const response = await DiscountService.getAll({
        isActive: true,
      });
      if (response?.data?.view?.data) {
        // Filter active discounts that are within date range
        const now = new Date();
        const activeDiscounts = response.data.view.data.filter((discount) => {
          const startDate = new Date(discount.startDate);
          const endDate = new Date(discount.endDate);
          return discount.isActive && now >= startDate && now <= endDate;
        });
        setDiscounts(activeDiscounts);
      }
    } catch (error) {
      console.error("Error fetching discounts:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDiscountSelect = (discount) => {
    if (!discount) {
      // No discount selected
      setSelectedDiscount(null);
      setDiscountType("none");
      setDiscountValue("");
      setDiscountCode("");
      return;
    }

    // Check if order meets minimum amount
    if (orderTotal < parseFloat(discount.minOrderAmount)) {
      alert(
        `Minimum order amount of Rs. ${parseFloat(discount.minOrderAmount).toFixed(2)} required for this discount`,
      );
      return;
    }

    setSelectedDiscount(discount);
    const normalizedType =
      discount.type === "fixed" ? "amount" : discount.type || "none";
    setDiscountType(normalizedType);
    setDiscountValue(discount.value);
    setDiscountCode(discount.code);
  };

  const getApplicableDiscounts = () => {
    return discounts.filter((d) => orderTotal >= parseFloat(d.minOrderAmount));
  };

  const applicableDiscounts = getApplicableDiscounts();

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        borderRadius: 2.5,
        background: `linear-gradient(145deg, ${alpha("#fff", 1)} 0%, ${alpha(theme.palette.warning.main, 0.02)} 100%)`,
        border: `1px solid ${alpha(theme.palette.warning.main, 0.15)}`,
        boxShadow: `0 2px 12px ${alpha(theme.palette.warning.main, 0.1)}`,
        transition: "all 0.3s ease",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        "&:hover": {
          boxShadow: `0 4px 20px ${alpha(theme.palette.warning.main, 0.15)}`,
          transform: "translateY(-2px)",
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          mb: 2.5,
          pb: 2.5,
          borderBottom: `2px solid ${alpha(theme.palette.warning.main, 0.12)}`,
          display: "flex",
          alignItems: "center",
          gap: 1.5,
        }}
      >
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: 2,
            background: `linear-gradient(135deg, ${theme.palette.warning.main} 0%, ${theme.palette.warning.dark} 100%)`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: `0 4px 12px ${alpha(theme.palette.warning.main, 0.3)}`,
          }}
        >
          <LocalOffer sx={{ fontSize: 20, color: "white" }} />
        </Box>
        <Box>
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 700,
              color: theme.palette.text.primary,
              fontSize: "0.95rem",
              mb: 0.3,
            }}
          >
            Apply Discount
          </Typography>
          <Typography
            variant="caption"
            sx={{
              fontWeight: 500,
              fontSize: "0.75rem",
              color: theme.palette.warning.main,
            }}
          >
            {applicableDiscounts.length} discount(s) available
          </Typography>
        </Box>
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress size={40} />
        </Box>
      ) : (
        <>
          {/* Quick Customer Discounts - Show when mobile number is entered */}
          {/* {hasCustomerMobile && (
            <Box sx={{ mb: 2.5 }}>
              <Paper
                elevation={0}
                sx={{
                  p: 2.5,
                  borderRadius: 2,
                  background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.06)} 0%, ${alpha(theme.palette.success.light, 0.04)} 100%)`,
                  border: `1.5px solid ${alpha(theme.palette.success.main, 0.25)}`,
                  boxShadow: `0 2px 8px ${alpha(theme.palette.success.main, 0.12)}`,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    mb: 2,
                  }}
                >
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: 1.5,
                      background: `linear-gradient(135deg, ${theme.palette.success.main} 0%, ${theme.palette.success.dark} 100%)`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      boxShadow: `0 2px 8px ${alpha(theme.palette.success.main, 0.25)}`,
                    }}
                  >
                    <Percent sx={{ fontSize: 16, color: "white" }} />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 700, lineHeight: 1.2 }}
                    >
                      Customer Discount
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontWeight: 500, fontSize: "0.7rem" }}
                    >
                      Quick apply or custom value (max 25%)
                    </Typography>
                  </Box>
                </Box>

                {/* Quick Discount Buttons 
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                    {[5, 10, 15, 20, 25].map((percentage) => {
                      const isActive =
                        !selectedDiscount &&
                        discountType === "percentage" &&
                        discountValue === percentage.toString();
                      return (
                        <Button
                          key={percentage}
                          variant={isActive ? "contained" : "outlined"}
                          onClick={() => handleQuickDiscount(percentage)}
                          sx={{
                            flex: 1,
                            minWidth: 55,
                            height: 38,
                            borderRadius: 1.5,
                            fontSize: "0.85rem",
                            fontWeight: 700,
                            borderWidth: 1.5,
                            borderColor: isActive
                              ? theme.palette.success.main
                              : alpha(theme.palette.success.main, 0.3),
                            bgcolor: isActive
                              ? theme.palette.success.main
                              : "white",
                            color: isActive
                              ? "white"
                              : theme.palette.success.main,
                            boxShadow: isActive
                              ? `0 3px 10px ${alpha(theme.palette.success.main, 0.3)}`
                              : "none",
                            "&:hover": {
                              borderWidth: 1.5,
                              borderColor: theme.palette.success.main,
                              bgcolor: isActive
                                ? theme.palette.success.dark
                                : alpha(theme.palette.success.main, 0.08),
                              boxShadow: `0 4px 14px ${alpha(theme.palette.success.main, 0.25)}`,
                              transform: "translateY(-1px)",
                            },
                            transition: "all 0.2s ease",
                          }}
                        >
                          {percentage}%
                        </Button>
                      );
                    })}
                  </Box>
                </Box>

                {/* Custom Discount Input 
                <Box>
                  <Stack direction="row" spacing={1.5}>
                    {/* Discount Type Selector 
                    <ButtonGroup
                      variant="outlined"
                      sx={{
                        borderRadius: 1.5,
                        "& .MuiButtonGroup-grouped": {
                          borderWidth: 1,
                        },
                      }}
                    >
                      <Button
                        onClick={() => {
                          setCustomDiscountMode("percentage");
                          setCustomInput("");
                          if (!selectedDiscount) {
                            setDiscountType("none");
                            setDiscountValue("");
                          }
                        }}
                        variant={
                          customDiscountMode === "percentage"
                            ? "contained"
                            : "outlined"
                        }
                        startIcon={<Percent sx={{ fontSize: 14 }} />}
                        sx={{
                          height: 38,
                          px: 1.5,
                          fontSize: "0.75rem",
                          fontWeight: 600,
                          borderWidth: 1,
                          borderColor: alpha(theme.palette.success.main, 0.3),
                          bgcolor:
                            customDiscountMode === "percentage"
                              ? theme.palette.success.main
                              : "white",
                          color:
                            customDiscountMode === "percentage"
                              ? "white"
                              : theme.palette.success.main,
                          "&:hover": {
                            borderWidth: 1,
                            borderColor: theme.palette.success.main,
                            bgcolor:
                              customDiscountMode === "percentage"
                                ? theme.palette.success.dark
                                : alpha(theme.palette.success.main, 0.08),
                          },
                        }}
                      >
                        %
                      </Button>
                      <Button
                        onClick={() => {
                          setCustomDiscountMode("amount");
                          setCustomInput("");
                          if (!selectedDiscount) {
                            setDiscountType("none");
                            setDiscountValue("");
                          }
                        }}
                        variant={
                          customDiscountMode === "amount"
                            ? "contained"
                            : "outlined"
                        }
                        startIcon={<CurrencyRupee sx={{ fontSize: 14 }} />}
                        sx={{
                          height: 38,
                          px: 1.5,
                          fontSize: "0.75rem",
                          fontWeight: 600,
                          borderWidth: 1,
                          borderColor: alpha(theme.palette.success.main, 0.3),
                          bgcolor:
                            customDiscountMode === "amount"
                              ? theme.palette.success.main
                              : "white",
                          color:
                            customDiscountMode === "amount"
                              ? "white"
                              : theme.palette.success.main,
                          "&:hover": {
                            borderWidth: 1,
                            borderColor: theme.palette.success.main,
                            bgcolor:
                              customDiscountMode === "amount"
                                ? theme.palette.success.dark
                                : alpha(theme.palette.success.main, 0.08),
                          },
                        }}
                      >
                        Rs
                      </Button>
                    </ButtonGroup>

                    {/* Custom Input Field
                    <TextField
                      fullWidth
                      value={customInput}
                      onChange={handleCustomInputChange}
                      placeholder={
                        customDiscountMode === "percentage" ? "0-25" : "Amount"
                      }
                      type="text"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            {customDiscountMode === "percentage" ? (
                              <Percent
                                sx={{
                                  color: theme.palette.success.main,
                                  fontSize: 16,
                                }}
                              />
                            ) : (
                              <CurrencyRupee
                                sx={{
                                  color: theme.palette.success.main,
                                  fontSize: 16,
                                }}
                              />
                            )}
                          </InputAdornment>
                        ),
                        sx: {
                          height: 38,
                          fontSize: "0.85rem",
                          fontWeight: 500,
                          borderRadius: 1.5,
                          bgcolor: "white",
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderWidth: 1,
                            borderColor: alpha(theme.palette.success.main, 0.3),
                          },
                          "&:hover .MuiOutlinedInput-notchedOutline": {
                            borderWidth: 1,
                            borderColor: alpha(theme.palette.success.main, 0.5),
                          },
                          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            borderWidth: 1.5,
                            borderColor: theme.palette.success.main,
                          },
                        },
                      }}
                    />
                  </Stack>
                </Box>
              </Paper>
              <Divider sx={{ my: 2.5 }} />
            </Box>
          )} */}

          {/* No Discount Option */}
          <Box sx={{ mb: 2.5 }}>
            <Paper
              elevation={0}
              onClick={() => handleDiscountSelect(null)}
              sx={{
                p: 2,
                cursor: "pointer",
                borderRadius: 2,
                border: `1.5px solid ${
                  !selectedDiscount && discountType === "none"
                    ? theme.palette.warning.main
                    : alpha(theme.palette.divider, 0.2)
                }`,
                bgcolor:
                  !selectedDiscount && discountType === "none"
                    ? alpha(theme.palette.warning.main, 0.08)
                    : "white",
                boxShadow:
                  !selectedDiscount && discountType === "none"
                    ? `0 3px 12px ${alpha(theme.palette.warning.main, 0.2)}`
                    : `0 1px 3px ${alpha(theme.palette.grey[400], 0.1)}`,
                transition: "all 0.2s ease",
                "&:hover": {
                  borderColor: alpha(theme.palette.warning.main, 0.6),
                  bgcolor: alpha(theme.palette.warning.main, 0.05),
                  boxShadow: `0 4px 16px ${alpha(theme.palette.warning.main, 0.15)}`,
                  transform: "translateY(-1px)",
                },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                {!selectedDiscount && discountType === "none" && (
                  <CheckCircle
                    sx={{ fontSize: 20, color: theme.palette.warning.main }}
                  />
                )}
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    No Discount
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontWeight: 500, fontSize: "0.75rem" }}
                  >
                    Continue without discount
                  </Typography>
                </Box>
              </Box>
            </Paper>
          </Box>

          {/* Available Discounts */}
          {discounts.length > 0 && (
            <>
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 600,
                  mb: 1.5,
                  display: "flex",
                  alignItems: "center",
                  gap: 0.8,
                  color: theme.palette.text.secondary,
                  textTransform: "uppercase",
                  letterSpacing: 0.5,
                  fontSize: "0.7rem",
                }}
              >
                <LocalOffer sx={{ fontSize: 14 }} />
                Promotional Discounts
              </Typography>
            </>
          )}

          <Box
            sx={{
              maxHeight: 360,
              overflowY: "auto",
              "&::-webkit-scrollbar": {
                width: "5px",
              },
              "&::-webkit-scrollbar-track": {
                bgcolor: "transparent",
              },
              "&::-webkit-scrollbar-thumb": {
                bgcolor: alpha(theme.palette.grey[400], 0.3),
                borderRadius: "10px",
                "&:hover": {
                  bgcolor: alpha(theme.palette.grey[400], 0.5),
                },
              },
            }}
          >
            <Grid container spacing={2}>
              {discounts.map((discount) => {
                const isApplicable =
                  orderTotal >= parseFloat(discount.minOrderAmount);
                const isSelected = selectedDiscount?.id === discount.id;

                return (
                  <Grid item size={{ xs: 12, lg: 6 }} key={discount.id}>
                    <Paper
                      elevation={0}
                      onClick={() =>
                        isApplicable && handleDiscountSelect(discount)
                      }
                      sx={{
                        p: 2,
                        cursor: isApplicable ? "pointer" : "not-allowed",
                        borderRadius: 2,
                        border: `1.5px solid ${
                          isSelected
                            ? theme.palette.warning.main
                            : isApplicable
                              ? alpha(theme.palette.divider, 0.2)
                              : alpha(theme.palette.grey[300], 0.3)
                        }`,
                        bgcolor: isSelected
                          ? alpha(theme.palette.warning.main, 0.08)
                          : isApplicable
                            ? "white"
                            : alpha(theme.palette.grey[100], 0.5),
                        opacity: isApplicable ? 1 : 0.6,
                        boxShadow: isSelected
                          ? `0 3px 12px ${alpha(theme.palette.warning.main, 0.2)}`
                          : isApplicable
                            ? `0 1px 3px ${alpha(theme.palette.grey[400], 0.1)}`
                            : "none",
                        transition: "all 0.2s ease",
                        "&:hover": isApplicable
                          ? {
                              borderColor: alpha(
                                theme.palette.warning.main,
                                0.6,
                              ),
                              bgcolor: alpha(theme.palette.warning.main, 0.04),
                              boxShadow: `0 4px 16px ${alpha(theme.palette.warning.main, 0.15)}`,
                              transform: "translateY(-1px)",
                            }
                          : {},
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: 1.5,
                        }}
                      >
                        {isSelected && (
                          <CheckCircle
                            sx={{
                              fontSize: 20,
                              color: theme.palette.warning.main,
                            }}
                          />
                        )}
                        <Box sx={{ flex: 1 }}>
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 700, mb: 0.8 }}
                          >
                            {discount.name}
                          </Typography>
                          <Box
                            sx={{
                              display: "flex",
                              gap: 0.8,
                              flexWrap: "wrap",
                              mb: 1,
                            }}
                          >
                            <Chip
                              label={discount.code}
                              size="small"
                              sx={{
                                height: 20,
                                fontSize: "0.65rem",
                                fontWeight: 600,
                                bgcolor: alpha(
                                  theme.palette.warning.main,
                                  0.12,
                                ),
                                color: theme.palette.warning.dark,
                              }}
                            />
                            <Chip
                              label={
                                discount?.type === "percentage"
                                  ? `${discount.value}% OFF`
                                  : `Rs. ${parseFloat(discount.value).toFixed(0)} OFF`
                              }
                              size="small"
                              sx={{
                                height: 20,
                                fontSize: "0.65rem",
                                fontWeight: 600,
                                bgcolor: alpha(
                                  theme.palette.success.main,
                                  0.12,
                                ),
                                color: theme.palette.success.dark,
                              }}
                            />
                          </Box>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                              display: "block",
                              mb: 0.5,
                              fontSize: "0.75rem",
                              fontWeight: 500,
                            }}
                          >
                            Min. order: Rs.{" "}
                            {parseFloat(discount.minOrderAmount).toFixed(0)}
                            {discount.maxDiscountAmount && (
                              <>
                                {" "}
                                • Max discount: Rs.{" "}
                                {parseFloat(discount.maxDiscountAmount).toFixed(
                                  0,
                                )}
                              </>
                            )}
                          </Typography>
                          {!isApplicable && (
                            <Typography
                              variant="caption"
                              sx={{
                                color: theme.palette.error.main,
                                fontWeight: 600,
                                fontSize: "0.7rem",
                              }}
                            >
                              Order amount below minimum requirement
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </Paper>
                  </Grid>
                );
              })}

              {discounts.length === 0 && (
                <Grid item size={{ xs: 12 }}>
                  <Box sx={{ textAlign: "center", py: 3 }}>
                    <LocalOffer
                      sx={{
                        fontSize: 40,
                        color: alpha(theme.palette.grey[400], 0.4),
                        mb: 1,
                      }}
                    />
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ fontWeight: 500, fontSize: "0.85rem" }}
                    >
                      No discounts available
                    </Typography>
                  </Box>
                </Grid>
              )}
            </Grid>
          </Box>
        </>
      )}
    </Paper>
  );
};

export default DiscountSelector;
